import { useEffect, useId, useRef, useState } from 'react';

/**
 * ★ VJ — seleção MÚLTIPLA numa lista suspensa com checkboxes (o projeto não
 * tinha nenhuma: os filtros são `<select>` simples).
 *
 * Botão com resumo ("Motivo", "1 motivo", "2 motivos") → lista com GRUPOS e
 * checkboxes; fecha com Esc (devolvendo o foco ao botão) e ao clicar fora (o
 * padrão do `BotaoReagir`). Acessibilidade mínima: `aria-haspopup="listbox"`,
 * `aria-expanded`, itens `role="option"` com `aria-selected`.
 *
 * @param {object} p
 * @param {string} p.rotulo               nome do filtro ("Motivo")
 * @param {{ id: string, rotulo: string, opcoes: { id: string, rotulo: string }[] }[]} p.grupos
 * @param {string[]} p.selecionados       ids marcados
 * @param {(ids: string[]) => void} p.aoMudar
 * @param {string} [p.unidade]            "motivo" → "2 motivos"
 * @param {string} [p.plural]             quando o plural NÃO é "+s"
 *
 * ★★ WK (29/set/2026) — o `plural` nasceu de um defeito real: o resumo fazia
 * `${unidade}s` e, com `unidade="origem"`, escreveu **"2 origems"** na tela.
 * Funcionava por acidente porque a única palavra usada até então era "motivo".
 * O padrão continua `+s` — quem passa "motivo" não muda nada.
 */
export default function MultiSelect({ rotulo, grupos = [], selecionados = [], aoMudar, unidade = 'item', plural = null }) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef(null);
  const botao = useRef(null);
  const idLista = useId();
  const marcados = new Set(selecionados);

  useEffect(() => {
    if (!aberto) return undefined;
    const forade = (e) => { if (raiz.current && !raiz.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', forade);
    return () => document.removeEventListener('mousedown', forade);
  }, [aberto]);

  function fecharComFoco() {
    setAberto(false);
    botao.current?.focus();
  }

  function alternar(id) {
    const proximo = new Set(marcados);
    proximo.has(id) ? proximo.delete(id) : proximo.add(id);
    aoMudar([...proximo]);
  }

  const n = marcados.size;
  const resumo = n === 0 ? rotulo : n === 1 ? `1 ${unidade}` : `${n} ${plural ?? `${unidade}s`}`;

  return (
    <div
      className="multi-select"
      ref={raiz}
      onKeyDown={(e) => { if (e.key === 'Escape' && aberto) { e.stopPropagation(); fecharComFoco(); } }}
    >
      <button
        type="button"
        ref={botao}
        className={n > 0 ? 'multi-select-botao ativo' : 'multi-select-botao'}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-controls={idLista}
        aria-label={rotulo}
        onClick={() => setAberto((a) => !a)}
      >
        {resumo} <span aria-hidden="true">▾</span>
      </button>

      {aberto && (
        <div className="multi-select-lista" id={idLista} role="listbox" aria-multiselectable="true" aria-label={rotulo}>
          {grupos.map((g) => (
            <div key={g.id} className="multi-select-grupo" role="group" aria-label={g.rotulo}>
              <p className="multi-select-titulo">{g.rotulo}</p>
              {g.opcoes.length === 0 && <p className="multi-select-vazio">—</p>}
              {g.opcoes.map((o) => (
                <label key={o.id} className="multi-select-opcao" role="option" aria-selected={marcados.has(o.id)}>
                  <input type="checkbox" checked={marcados.has(o.id)} onChange={() => alternar(o.id)} />
                  {o.rotulo}
                </label>
              ))}
            </div>
          ))}
          {n > 0 && (
            <button type="button" className="btn-texto multi-select-limpar" onClick={() => aoMudar([])}>
              Limpar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
